<template>
	<div class="buybox-no-offers">
		<div class="buybox-no-offers__text">
			<p class="buybox-no-offers__text__title">
				<slot>
					{{
						filter === 'free'
							? t('WEBAPP_NO_FREE_OFFER', { title: titleName })
							: t('WEBAPP_NO_STREAMING_OFFER', { title: titleName })
					}}
				</slot>
			</p>

			<p class="buybox-no-offers__text__subtitle" v-if="showSubtitle">{{ t('WEBAPP_NOTIFY_WHEN_AVAIL') }}</p>
		</div>
		<BasicButton class="buybox-no-offers__button" @click="onNotifyMe">
			<div class="buybox-no-offers__button__content">
				<FontAwesomeIcon
					class="buybox-no-offers__button__content__icon"
					:class="{ primary: isTitleInWatchlist }"
					:icon="isTitleInWatchlist ? jwCheckIcon : faBell"
					size="2x"
					fixed-width
				/>
				<span>{{ t('WEBAPP_BUTTON_NOTIFY_ME') }}</span>
			</div>
		</BasicButton>
	</div>
</template>

<script setup lang="ts">
import { SnowplowContext } from '@/helpers/tracking/providers';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed } from 'vue';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { jwCheckIcon } from '@/plugins/global-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';

const BasicButton = defineAsyncComponent(() => import('@/ui-components/BasicButton.vue'));

const { t } = useI18n();

const props = withDefaults(
	defineProps<{
		isTitleInWatchlist: boolean;
		title: TitleDetail;
		filter: BuyBoxFilterOptions;
		additionalContexts: SnowplowContext[];
		//INTERNATIONAL_OFFERS_EXP
		showSubtitle?: boolean;
		//INTERNATIONAL_OFFERS_EXP
	}>(),
	{
		isTitleInWatchlist: false,
		additionalContexts: () => [],
		showSubtitle: true,
	}
);

const emit = defineEmits<{
	(e: 'onNotifyMe'): void;
}>();

const onNotifyMe = () => {
	emit('onNotifyMe');

	let offerType = '';
	switch (props.filter) {
		case 'free':
			offerType = 'free';
			break;
		case 'cinema':
			offerType = 'cinema';
			break;
		default:
			offerType = 'streaming';
			break;
	}

	TrackingHelper.trackEvent('userinteraction', {
		action: 'notify_me_clicked',
		label: offerType,
		value: !props.isTitleInWatchlist ? 1 : 0,
	});
};

const titleName = computed(() => props.title.content.title);
</script>

<style lang="scss" scoped>
.buybox-no-offers {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px;
	background: #111924;
	height: 104px;

	@media (max-width: 767px) {
		flex-direction: column;
		align-items: center;
		gap: 16px;
		height: auto;
		justify-content: flex-start;
	}

	&__text {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: flex-start;
		text-align: start;

		@media (max-width: 767px) {
			text-align: center;
		}

		&__title {
			width: 100%;
			margin: 0;
			margin-bottom: 8px;
			color: #c6c8cd;
			font-size: 14px;
			font-weight: 900;
			line-height: 24px;
			letter-spacing: 0.24px;
		}

		&__subtitle {
			margin: 0;
			color: #c6c8cd;
			font-size: 14px;
			line-height: 16px;
			letter-spacing: 0.21px;
		}
	}
	&__button {
		height: 40px;
		min-width: 166px;
		padding: 8px 16px;
		border-radius: 8px;
		border: none;
		background: #303740;
		color: #c6c8cd;
		font-size: 16px;
		font-weight: 900;
		letter-spacing: 0.24px;
		cursor: pointer;

		&:hover {
			background: #303740cb;
		}

		@media (max-width: 1100px) {
			min-width: 110px;
		}

		@media (max-width: 1050px) {
			min-width: 166px;
		}

		@media (max-width: 800px) {
			width: fit-content;
		}

		&__content {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 8px;

			&__icon {
				height: 15px;
				width: 15px;
				&.primary {
					color: var(--jw-yellow-500);
				}
			}
		}
	}
}

.jw-logo {
	width: 16px;
	height: 16px;
}
</style>
